import { useCallback, useEffect, useRef } from 'react';
import {
  ownershipQueueRequiresRawTable,
  type IncidentOpsQueueFilter,
} from '../../domain/incidents/incidentOwnershipQueueFilter';
import {
  pickGroupedEvidenceRepresentativeIncident,
} from '../../domain/incidents/groupedEvidenceRepresentativePick';
import { buildFallbackGroupContextFromIncident } from '../../domain/incidents/incidentPatternVocabulary';
import { findSafetyDashboardWorkflowRowForReview } from '../../domain/incidents/riskPatternWorkflowReviewContext';
import { scopeIncidentToEpisodeEvidence } from '../../domain/incidents/observationEpisodeRow';
import { incidentsApi, type Incident } from '../../services/incidentsApi';
import { reportsApi } from '../../services/reportsApi';
import { scheduleDeferredWork } from '../../utils/deferredWork';
import { buildIncidentReviewDeepLinkLoadKey } from './incidentReviewDeepLink';
import type { IncidentWorkflowStatusFilter } from './useIncidentsFilters';

type SetSearchParams = (next: URLSearchParams, options?: { replace?: boolean }) => void;
type FetchDataOptions = { showLoading?: boolean; skipPersistedRefresh?: boolean };

const _documentIsVisible = () => typeof document === 'undefined' || document.visibilityState === 'visible';

type UseIncidentsPageOrchestrationArgs = {
  pendingQueueAutoOpen: boolean;
  setPendingQueueAutoOpen: (value: boolean) => void;
  loading: boolean;
  tableMode: 'raw' | 'grouped';
  filterOpsQueue: string;
  visibleRows: any[];
  viewDialogOpen: boolean;
  selectedIncident: Incident | null;
  incidents: Incident[];
  filteredIncidents: Incident[];
  searchIncidentId: string;
  setSelectedGroupContext: (value: any) => void;
  setSelectedIncident: (value: Incident | null) => void;
  setViewDialogTab: (value: number) => void;
  setViewDialogOpen: (value: boolean) => void;
  fetchData: (opts?: FetchDataOptions) => Promise<void>;
  clearLoading: () => void;
  setIncidents: (value: Incident[] | ((prev: Incident[]) => Incident[])) => void;
  createDialogOpen: boolean;
  searchParams: URLSearchParams;
  setSearchParams: SetSearchParams;
  filterCameraId: string;
  setFilterCameraId: (value: string) => void;
  filterDeviceId: string;
  setFilterDeviceId: (value: string) => void;
  filterZoneId: string;
  setFilterZoneId: (value: string) => void;
  filterIncidentType: string;
  setFilterIncidentType: (value: string) => void;
  filterWorkflowStatus: IncidentWorkflowStatusFilter;
  setFilterWorkflowStatus: (value: IncidentWorkflowStatusFilter) => void;
  filterHeatmapHour: number | null;
  setFilterHeatmapHour: (value: number | null) => void;
  filterPolicyPack: string;
  setFilterPolicyPack: (value: string) => void;
  setFilterSeverity: (value: any) => void;
  setTableMode: (value: 'raw' | 'grouped') => void;
  setTabValue: (value: number) => void;
  setFilterOpsQueue: (value: any) => void;
  setSortModel?: (value: Array<{ field: string; sort: 'asc' | 'desc' }>) => void;
  setSortBySeverity?: (value: boolean) => void;
  customFrom: string;
  customTo: string;
  setTimePreset: (value: any) => void;
  setCustomFrom: (value: string) => void;
  setCustomTo: (value: string) => void;
  allCameras: Array<{ camera_id?: string | null }>;
  isUuid: (value: string) => boolean;
  toLocalInputValue: (value?: string) => string;
  setError: (value: string | null) => void;
  tabValue: number;
  filterSeverity: string;
  timeRange: {
    fromDate?: string;
    toDate?: string;
  };
  setDrawerIncidentLoading: (value: boolean) => void;
  scopedSiteId: string;
  scopedOrgId: string;
};

export function useIncidentsPageOrchestration({
  pendingQueueAutoOpen,
  setPendingQueueAutoOpen,
  loading,
  tableMode,
  filterOpsQueue,
  visibleRows,
  viewDialogOpen,
  selectedIncident,
  incidents,
  filteredIncidents,
  searchIncidentId,
  setSelectedGroupContext,
  setSelectedIncident,
  setViewDialogTab,
  setViewDialogOpen,
  fetchData,
  clearLoading,
  setIncidents,
  createDialogOpen,
  searchParams,
  setSearchParams,
  filterCameraId,
  setFilterCameraId,
  filterDeviceId,
  setFilterDeviceId,
  filterZoneId,
  setFilterZoneId,
  filterIncidentType,
  setFilterIncidentType,
  filterWorkflowStatus,
  setFilterWorkflowStatus,
  filterHeatmapHour,
  setFilterHeatmapHour,
  filterPolicyPack,
  setFilterPolicyPack,
  setFilterSeverity,
  setTableMode,
  setTabValue,
  setFilterOpsQueue,
  setSortModel,
  setSortBySeverity,
  customFrom,
  customTo,
  setTimePreset,
  setCustomFrom,
  setCustomTo,
  allCameras,
  isUuid,
  toLocalInputValue,
  setError,
  tabValue,
  filterSeverity,
  timeRange,
  setDrawerIncidentLoading,
  scopedSiteId,
  scopedOrgId,
}: UseIncidentsPageOrchestrationArgs) {
  const previousUrlFiltersRef = useRef<{
    cameraId: string;
    deviceId: string;
    zoneId: string;
    incidentType: string;
    workflowStatus: string;
    severity: string;
    opsQueue: string;
    policyPack: string;
  }>({
    cameraId: '',
    deviceId: '',
    zoneId: '',
    incidentType: '',
    workflowStatus: '',
    severity: '',
    opsQueue: '',
    policyPack: '',
  });
  const lastIncidentDeepLinkLoadRef = useRef<string>('');

  const openGroupKeyReviewLink = useCallback(async (groupKey: string) => {
    const normalizedGroupKey = String(groupKey || '').trim();
    if (!normalizedGroupKey) return;
    const parts = normalizedGroupKey.split('|').map((part) => part.trim());
    const queryIncidentType = String(searchParams.get('incident_type') || '').trim();
    const queryZoneId = String(searchParams.get('zone_id') || '').trim();
    const queryCameraId = String(searchParams.get('camera_id') || '').trim();
    const queryDeviceId = String(searchParams.get('device_id') || '').trim();
    const parsedOffset = parts.length >= 5 ? 1 : 0;
    const incidentType = queryIncidentType || parts[parsedOffset] || '';
    const zoneId = queryZoneId || parts[parsedOffset + 1] || '';
    const cameraId = queryCameraId || parts[parsedOffset + 2] || '';
    const deviceId = queryDeviceId || parts[parsedOffset + 3] || '';
    if (!incidentType || !zoneId || !cameraId || !deviceId) return;

    setSelectedIncident(null);
    setViewDialogTab(0);
    setViewDialogOpen(true);
    setDrawerIncidentLoading(true);
    try {
      const grouped = await incidentsApi.listIncidentGroups({
        status: 'all',
        from_date: timeRange.fromDate || undefined,
        to_date: timeRange.toDate || undefined,
        incident_type: incidentType,
        zone_id: zoneId,
        camera_id: cameraId,
        device_id: deviceId,
        limit: 50,
        prefer_persisted: true,
      });
      const matchingGroup = (grouped?.items || []).find((candidate: any) =>
        String(candidate?.group_key || '').trim() === normalizedGroupKey
      ) || null;
      if (!matchingGroup) return;
      setSelectedGroupContext(matchingGroup);

      const serverAnchorIncidentId = String(matchingGroup?.review_anchor_incident_id || '').trim();
      const hasServerAnchorDecision = Boolean(
        String(matchingGroup?.review_anchor_selection_reason || '').trim() && serverAnchorIncidentId,
      );
      if (hasServerAnchorDecision) {
        const incident = await incidentsApi.getIncident(serverAnchorIncidentId);
        setSelectedIncident(incident);
      }
    } catch {
      // Non-fatal: leave the exact grouped review drawer open when the group can be shown without an anchor incident.
    } finally {
      setDrawerIncidentLoading(false);
    }
  }, [
    searchParams,
    setDrawerIncidentLoading,
    setSelectedGroupContext,
    setSelectedIncident,
    setViewDialogOpen,
    setViewDialogTab,
    timeRange.fromDate,
    timeRange.toDate,
  ]);

  useEffect(() => {
    const reviewMode = String(searchParams.get('review') || '').trim() === '1';
    if (!reviewMode) return;
    if (String(searchParams.get('incident_id') || '').trim()) return;
    const groupKey = String(searchParams.get('group_key') || searchParams.get('workflow_id') || '').trim();
    if (!groupKey) return;
    const deepLinkLoadKey = `review:group:${groupKey}:${timeRange.fromDate || ''}:${timeRange.toDate || ''}`;
    if (lastIncidentDeepLinkLoadRef.current === deepLinkLoadKey) return;
    lastIncidentDeepLinkLoadRef.current = deepLinkLoadKey;
    void openGroupKeyReviewLink(groupKey);
  }, [
    openGroupKeyReviewLink,
    searchParams,
    timeRange.fromDate,
    timeRange.toDate,
  ]);

  useEffect(() => {
    const shouldAutoOpen = pendingQueueAutoOpen || (Boolean(filterOpsQueue) && !selectedIncident);
    if (!shouldAutoOpen) return;
    if (loading) return;
    if (tableMode !== 'raw') return;
    if (!filterOpsQueue) {
      if (pendingQueueAutoOpen) setPendingQueueAutoOpen(false);
      return;
    }
    if (viewDialogOpen) {
      if (pendingQueueAutoOpen) setPendingQueueAutoOpen(false);
      return;
    }
    const rows = (visibleRows as Incident[] | any[]) || [];
    if (rows.length === 0) {
      if ((incidents || []).length > 0 && (filteredIncidents || []).length === 0 && !String(searchIncidentId || '').trim()) {
        return;
      }
      if (pendingQueueAutoOpen) setPendingQueueAutoOpen(false);
      return;
    }
    const first = rows[0] as Incident | undefined;
    if (first?.incident_id) {
      setSelectedGroupContext(null);
      setSelectedIncident(first);
      setViewDialogTab(0);
      setViewDialogOpen(true);
    }
    if (pendingQueueAutoOpen) setPendingQueueAutoOpen(false);
  }, [
    pendingQueueAutoOpen,
    loading,
    tableMode,
    filterOpsQueue,
    visibleRows,
    viewDialogOpen,
    selectedIncident,
    incidents,
    filteredIncidents,
    searchIncidentId,
    setPendingQueueAutoOpen,
    setSelectedGroupContext,
    setSelectedIncident,
    setViewDialogTab,
    setViewDialogOpen,
  ]);

  const skipInitialQueueFetchRef = useRef(
    String(searchParams.get('review') || '').trim() === '1'
      && Boolean(String(searchParams.get('incident_id') || '').trim()),
  );

  useEffect(() => {
    if (skipInitialQueueFetchRef.current) {
      skipInitialQueueFetchRef.current = false;
      clearLoading();
      scheduleDeferredWork(() => {
        void fetchData({ showLoading: false, skipPersistedRefresh: true });
      }, { timeoutMs: 250 });
      return;
    }
    void fetchData();
  }, [clearLoading, fetchData]);

  useEffect(() => {
    const reviewMode = String(searchParams.get('review') || '').trim() === '1';
    const incidentId = String(searchParams.get('incident_id') || '').trim();
    const selectedId = String(selectedIncident?.incident_id || '').trim();
    if (!reviewMode || !incidentId || !selectedId) return;

    const status =
      selectedIncident?.status === 'CLOSED' || selectedIncident?.status === 'SUPPRESSED' ? 'closed' : 'open';
    const incidentType = selectedIncident?.incident_type || undefined;
    const zoneId = selectedIncident?.zone_id || undefined;
    const deviceId = selectedIncident?.device_id || undefined;
    const cameraId = selectedIncident?.camera_id || undefined;
    let cancelled = false;
    scheduleDeferredWork(() => {
      if (cancelled) return;
      void (async () => {
        try {
          const res = await incidentsApi.listIncidents(
            status,
            undefined,
            25,
            0,
            timeRange.fromDate,
            timeRange.toDate,
            incidentType,
            zoneId,
            deviceId,
            cameraId,
            { skip_total: true, summary: true },
          );
          if (cancelled) return;
          setIncidents(res.incidents || []);
        } catch {
          // series rail is optional; keep review shell responsive
        }
      })();
    }, { timeoutMs: 900 });

    return () => {
      cancelled = true;
    };
  }, [
    searchParams,
    selectedIncident?.camera_id,
    selectedIncident?.device_id,
    selectedIncident?.incident_id,
    selectedIncident?.incident_type,
    selectedIncident?.status,
    selectedIncident?.zone_id,
    setIncidents,
    timeRange.fromDate,
    timeRange.toDate,
  ]);

  useEffect(() => {
    if (createDialogOpen) return;
    const reviewMode = String(searchParams.get('review') || '').trim() === '1';
    // Review pages already poll the selected incident for pending proof; a 10s full-queue
    // refresh here remounts the shell and feels like the page is thrashing.
    if (reviewMode) return;
    const refreshQuietly = () => {
      if (!_documentIsVisible()) return;
      try {
        void fetchData({ showLoading: false, skipPersistedRefresh: true });
      } catch {
        // ignore
      }
    };
    const handleWindowFocus = () => {
      refreshQuietly();
    };
    const handleVisibilityChange = () => {
      if (_documentIsVisible()) refreshQuietly();
    };
    const intervalId = window.setInterval(() => {
      refreshQuietly();
    }, 10_000);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      try {
        window.clearInterval(intervalId);
      } catch {
        // ignore
      }
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [createDialogOpen, fetchData, searchParams]);

  useEffect(() => {
    const cameraId = (searchParams.get('camera_id') || '').trim();
    const zoneId = (searchParams.get('zone_id') || '').trim();
    const deviceRaw = (searchParams.get('device') || searchParams.get('device_id') || '').trim();
    const deviceId = deviceRaw && isUuid(deviceRaw) ? '' : deviceRaw;
    const incidentType = (searchParams.get('incident_type') || '').trim();
    const workflowStatusRaw = (searchParams.get('workflow_status') || '').trim();
    const workflowStatus =
      workflowStatusRaw === 'Needs review'
      || workflowStatusRaw === 'Assigned'
      || workflowStatusRaw === 'Needs action'
      || workflowStatusRaw === 'Resolved'
      || workflowStatusRaw === 'Reviewed'
      || workflowStatusRaw === 'Suppressed'
      || workflowStatusRaw === 'Unmapped'
        ? workflowStatusRaw
        : '';
    const severityRaw = (searchParams.get('severity') || '').trim().toLowerCase();
    const severity =
      severityRaw === 'critical' || severityRaw === 'high' || severityRaw === 'medium' || severityRaw === 'low'
        ? severityRaw
        : '';
    const tableModeRaw = (searchParams.get('table_mode') || searchParams.get('tableMode') || '').trim();
    const tableModeParam = tableModeRaw === 'raw' || tableModeRaw === 'grouped' ? tableModeRaw : '';
    const tabRaw = (searchParams.get('tab') || '').trim().toLowerCase();
    const tabParam = tabRaw === 'closed' || tabRaw === '2' ? 2 : tabRaw === 'open' || tabRaw === '1' ? 1 : 0;
    const hasTabParam = tabRaw === 'closed' || tabRaw === '2' || tabRaw === 'open' || tabRaw === '1' || tabRaw === 'all' || tabRaw === '0';
    const opsQueueRaw = (searchParams.get('ops_queue') || searchParams.get('opsQueue') || '').trim();
    const opsQueue =
      opsQueueRaw === 'my_queue'
      || opsQueueRaw === 'my_risk_queue'
      || opsQueueRaw === 'assigned_by_me'
      || opsQueueRaw === 'risk_assigned_by_me'
      || opsQueueRaw === 'escalations'
      || opsQueueRaw === 'overdue'
      || opsQueueRaw === 'unassigned_high_risk'
        ? (opsQueueRaw as IncidentOpsQueueFilter)
        : '';
    const heatmapHourRaw = (searchParams.get('heatmap_hour') || '').trim();
    const heatmapHourNum = /^(?:[0-9]|1[0-9]|2[0-3])$/.test(heatmapHourRaw) ? Number(heatmapHourRaw) : NaN;
    const heatmapFromUrl = !Number.isNaN(heatmapHourNum) && heatmapHourNum >= 0 && heatmapHourNum <= 23 ? heatmapHourNum : null;
    const policyPack = (searchParams.get('policy_pack') || '').trim();
    const fromDateParam = (searchParams.get('from_date') || '').trim();
    const toDateParam = (searchParams.get('to_date') || '').trim();
    const legacyReportFromParam = (searchParams.get('report_from') || '').trim();
    const legacyReportToParam = (searchParams.get('report_to') || '').trim();
    const cameraKnown = !!cameraId && (allCameras || []).some((camera: any) => String(camera?.camera_id || '').trim() === cameraId);
    const previousUrlFilters = previousUrlFiltersRef.current;

    // Apply camera scope from the URL even before the camera catalog loads.
    // Waiting on cameraKnown previously dropped Overview deep-links and opened
    // an unfiltered Investigate page.
    if (cameraId && !filterCameraId) setFilterCameraId(cameraId);
    if (deviceId && !filterDeviceId) setFilterDeviceId(deviceId);
    if (zoneId && !filterZoneId) setFilterZoneId(zoneId);
    if (incidentType && !filterIncidentType) setFilterIncidentType(incidentType);
    if (workflowStatus && !filterWorkflowStatus) setFilterWorkflowStatus(workflowStatus);
    if (severity && !filterSeverity) setFilterSeverity(severity);
    if (tableModeParam && tableModeParam !== tableMode) setTableMode(tableModeParam);
    if (hasTabParam && tabParam !== tabValue) setTabValue(tabParam);
    if (opsQueue && !filterOpsQueue) {
      if (!tableModeParam && ownershipQueueRequiresRawTable(opsQueue)) setTableMode('raw');
      setFilterOpsQueue(opsQueue);
      setPendingQueueAutoOpen(true);
    }
    if (policyPack && !filterPolicyPack) {
      if (!tableModeParam) setTableMode('grouped');
      setFilterPolicyPack(policyPack);
    }
    const reviewFromUrl = String(searchParams.get('review') || '').trim() === '1';
    if (heatmapFromUrl !== null) {
      if (!reviewFromUrl && !tableModeParam) setTableMode('raw');
      if (filterHeatmapHour !== heatmapFromUrl) setFilterHeatmapHour(heatmapFromUrl);
    } else if (filterHeatmapHour !== null && !reviewFromUrl) {
      setFilterHeatmapHour(null);
    }
    if (fromDateParam && toDateParam && !customFrom && !customTo) {
      const fromLocal = toLocalInputValue(fromDateParam);
      const toLocal = toLocalInputValue(toDateParam);
      if (fromLocal && toLocal) {
        setTimePreset('custom');
        setCustomFrom(fromLocal);
        setCustomTo(toLocal);
      }
    } else if (!fromDateParam && !toDateParam && legacyReportFromParam && legacyReportToParam && !customFrom && !customTo) {
      const fromLocal = toLocalInputValue(legacyReportFromParam);
      const toLocal = toLocalInputValue(legacyReportToParam);
      if (fromLocal && toLocal) {
        setTimePreset('custom');
        setCustomFrom(fromLocal);
        setCustomTo(toLocal);
      }
    }
    const sortParam = (searchParams.get('sort') || '').trim().toLowerCase();
    if (sortParam === 'oldest') {
      setSortBySeverity?.(false);
      setSortModel?.([{ field: 'created_at', sort: 'asc' }]);
    }
    // Only strip unknown camera_ids after the catalog has loaded — never while
    // allCameras is still empty (that raced Overview scoped evidence links away).
    if (
      cameraId
      && !cameraKnown
      && (allCameras || []).length > 0
      && searchParams.get('camera_id')
    ) {
      const next = new URLSearchParams(searchParams);
      next.delete('camera_id');
      setSearchParams(next, { replace: true });
    }
    if (deviceRaw && isUuid(deviceRaw) && searchParams.get('device_id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('device_id');
      setSearchParams(next, { replace: true });
    }

    // When a previously scoped incidents URL is relaxed back to site-only view,
    // clear the URL-derived in-memory filters so the page truly broadens again.
    if (!cameraId && previousUrlFilters.cameraId && filterCameraId === previousUrlFilters.cameraId) {
      setFilterCameraId('');
    }
    if (!deviceId && previousUrlFilters.deviceId && filterDeviceId === previousUrlFilters.deviceId) {
      setFilterDeviceId('');
    }
    if (!zoneId && previousUrlFilters.zoneId && filterZoneId === previousUrlFilters.zoneId) {
      setFilterZoneId('');
    }
    if (!incidentType && previousUrlFilters.incidentType && filterIncidentType === previousUrlFilters.incidentType) {
      setFilterIncidentType('');
    }
    if (!workflowStatus && previousUrlFilters.workflowStatus && filterWorkflowStatus === previousUrlFilters.workflowStatus) {
      setFilterWorkflowStatus('');
    }
    if (!severity && previousUrlFilters.severity && filterSeverity === previousUrlFilters.severity) {
      setFilterSeverity('');
    }
    if (!opsQueue && previousUrlFilters.opsQueue && filterOpsQueue === previousUrlFilters.opsQueue) {
      setFilterOpsQueue('');
    }
    if (!policyPack && previousUrlFilters.policyPack && filterPolicyPack === previousUrlFilters.policyPack) {
      setFilterPolicyPack('');
    }

    previousUrlFiltersRef.current = {
      cameraId,
      deviceId,
      zoneId,
      incidentType,
      workflowStatus,
      severity,
      opsQueue,
      policyPack,
    };
  }, [
    searchParams,
    setSearchParams,
    filterCameraId,
    filterDeviceId,
    filterZoneId,
    filterIncidentType,
    filterWorkflowStatus,
    filterSeverity,
    filterOpsQueue,
    filterHeatmapHour,
    setFilterHeatmapHour,
    filterPolicyPack,
    setFilterPolicyPack,
    tableMode,
    customFrom,
    customTo,
    allCameras,
    isUuid,
    toLocalInputValue,
    setFilterCameraId,
    setFilterDeviceId,
    setFilterZoneId,
    setFilterIncidentType,
    setFilterWorkflowStatus,
    setFilterSeverity,
    setTableMode,
    setTabValue,
    setFilterOpsQueue,
    setPendingQueueAutoOpen,
    setSortModel,
    setSortBySeverity,
    setTimePreset,
    setCustomFrom,
    setCustomTo,
    tabValue,
  ]);

  useEffect(() => {
    const cameraId = String(filterCameraId || '').trim();
    if (!cameraId) return;
    if (!Array.isArray(allCameras) || allCameras.length === 0) return;
    const valid = allCameras.some((camera: any) => String(camera?.camera_id || '').trim() === cameraId);
    if (!valid) setFilterCameraId('');
  }, [filterCameraId, allCameras, setFilterCameraId]);

  useEffect(() => {
    const incidentId = String(searchParams.get('incident_id') || '').trim();
    if (!incidentId) return;
    const episodeId = String(searchParams.get('episode_id') || '').trim();
    const reviewMode = String(searchParams.get('review') || '').trim() === '1';
    const exactIncident = String(searchParams.get('exact_incident') || '').trim() === '1';
    const heatmapHour = String(searchParams.get('heatmap_hour') || '').trim();
    const heatmapHourActive = Boolean(heatmapHour);
    const preserveExactOccurrence =
      exactIncident || heatmapHourActive;
    const deepLinkLoadKey = buildIncidentReviewDeepLinkLoadKey({
      reviewMode,
      preserveExactOccurrence,
      incidentId: episodeId ? `${incidentId}::${episodeId}` : incidentId,
      fromDate: timeRange.fromDate,
      toDate: timeRange.toDate,
      heatmapHour,
    });
    // Optimistic deep-link stubs may include workflow_id camera/device seeds. Only treat the
    // selection as hydrated when API-only fields are present, otherwise F5 short-circuits
    // before series reconcile can swap to newer evidence-complete proof.
    const selectedId = String(selectedIncident?.incident_id || '').trim();
    const alreadyShowingTarget = selectedId === incidentId;
    const selectedIsHydrated = alreadyShowingTarget && Boolean(
      String((selectedIncident as any)?.created_at || '').trim()
      || String((selectedIncident as any)?.snapshot_status || '').trim()
      || String((selectedIncident as any)?.clip_status || '').trim(),
    );
    if (selectedIsHydrated && lastIncidentDeepLinkLoadRef.current === deepLinkLoadKey) {
      setViewDialogTab(0);
      setViewDialogOpen(true);
      setDrawerIncidentLoading(false);
      return;
    }
    lastIncidentDeepLinkLoadRef.current = deepLinkLoadKey;
    let cancelled = false;
    const workflowParts = String(searchParams.get('workflow_id') || '')
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean);
    const optimisticIncident = {
      incident_id: incidentId,
      ...(episodeId
        ? {
            episode_id: episodeId,
            observation_episode_id: episodeId,
            snapshot_uri: null,
            clip_uri: null,
          }
        : {}),
      ...(workflowParts.length >= 4
        ? {
            incident_type: workflowParts[0],
            zone_id: workflowParts[1],
            camera_id: workflowParts[2],
            device_id: workflowParts[3],
          }
        : {}),
    } as Incident;
    setViewDialogTab(0);
    setViewDialogOpen(true);
    // Never replace an already-open incident with a sparse stub — that remounts evidence
    // and yanks scroll when heatmap_hour / review_focus params change.
    if (!alreadyShowingTarget) {
      setSelectedIncident(optimisticIncident);
      if (reviewMode) {
        setSelectedGroupContext(buildFallbackGroupContextFromIncident(optimisticIncident));
      } else {
        setSelectedGroupContext(null);
      }
      setDrawerIncidentLoading(true);
    }
    void (async () => {
      try {
        let incident = (
          alreadyShowingTarget && selectedIsHydrated && selectedIncident
            ? selectedIncident
            : await incidentsApi.getIncident(incidentId)
        );
        if (cancelled) return;
        if (episodeId) {
          let evidenceItems: Array<Record<string, any>> = [];
          try {
            evidenceItems = await incidentsApi.listIncidentEvidence(incidentId);
          } catch {
            evidenceItems = [];
          }
          if (cancelled) return;
          incident = scopeIncidentToEpisodeEvidence(incident, episodeId, evidenceItems);
        }

        if (!alreadyShowingTarget || !selectedIsHydrated) {
          setSelectedIncident(incident);
        }
        if (reviewMode) {
          if (!alreadyShowingTarget) {
            setSelectedGroupContext(buildFallbackGroupContextFromIncident(incident));
          }
        } else if (!alreadyShowingTarget) {
          setSelectedGroupContext(null);
        }

        if (!reviewMode) return;

        void (async () => {
          try {
            const grouped = await incidentsApi.listIncidentGroups({
              status: incident?.status === 'CLOSED' || incident?.status === 'SUPPRESSED' ? 'closed' : 'open',
              from_date: timeRange.fromDate || undefined,
              to_date: timeRange.toDate || undefined,
              incident_type: incident?.incident_type || undefined,
              zone_id: incident?.zone_id || undefined,
              device_id: incident?.device_id || undefined,
              camera_id: incident?.camera_id || undefined,
              limit: 50,
              prefer_persisted: true,
            });
            if (cancelled) return;
            const matchingGroup = (grouped?.items || []).find((candidate: any) =>
              String(candidate?.incident_type || '').trim() === String(incident?.incident_type || '').trim()
              && String(candidate?.zone_id || '').trim() === String(incident?.zone_id || '').trim()
              && String(candidate?.camera_id || '').trim() === String(incident?.camera_id || '').trim()
              && String(candidate?.device_id || '').trim() === String(incident?.device_id || '').trim()
            ) || null;
            if (matchingGroup) {
              setSelectedGroupContext(matchingGroup);
            }
            const latestLinkedIncidentId = String(
              matchingGroup?.linked_detailed_record_id || matchingGroup?.incident_id || incident?.incident_id || ''
            ).trim();
            let reviewRepresentativeIncidentId = String(
              matchingGroup?.review_anchor_incident_id || latestLinkedIncidentId,
            ).trim();
            const hasServerAnchorDecision = Boolean(
              String(matchingGroup?.review_anchor_selection_reason || '').trim()
              && reviewRepresentativeIncidentId,
            );
            // Always pick newest evidence-complete in-series for non-exact review deep links,
            // even when the deep-linked row already has cloud proof (stale after outages).
            if (!preserveExactOccurrence && !hasServerAnchorDecision) {
              try {
                const listed = await incidentsApi.listIncidents(
                  undefined,
                  undefined,
                  50,
                  0,
                  timeRange.fromDate || undefined,
                  timeRange.toDate || undefined,
                  incident?.incident_type || undefined,
                  incident?.zone_id || undefined,
                  incident?.device_id || undefined,
                  incident?.camera_id || undefined,
                );
                if (cancelled) return;
                const decision = pickGroupedEvidenceRepresentativeIncident(
                  listed?.incidents || [],
                  {
                    fallbackId: latestLinkedIncidentId || String(incident?.incident_id || '').trim(),
                    preferCloudProof: true,
                    patternKey: String(matchingGroup?.pattern_key || incident?.pattern_key || '').trim(),
                  },
                );
                setSelectedGroupContext({
                  ...(matchingGroup || buildFallbackGroupContextFromIncident(incident) || {}),
                  review_anchor_incident_id: decision.reviewAnchorIncidentId,
                  review_anchor_selection_reason: decision.reviewAnchorSelectionReason,
                  latest_incident_id: decision.latestIncidentId,
                  latest_incident_evidence_state: decision.latestIncidentEvidenceState,
                  anchor_reuse_allowed: decision.anchorReuseAllowed,
                });
                const picked = decision.reviewAnchorIncidentId;
                if (picked) reviewRepresentativeIncidentId = picked;
              } catch {
                // Non-fatal: keep latest linked representative from the group row.
              }
            }
            if (
              !preserveExactOccurrence
              && reviewRepresentativeIncidentId
              && reviewRepresentativeIncidentId !== String(incident?.incident_id || '').trim()
            ) {
              incident = await incidentsApi.getIncident(reviewRepresentativeIncidentId);
              if (cancelled) return;
              setSelectedIncident(incident);
              const next = new URLSearchParams(searchParams);
              next.set('incident_id', reviewRepresentativeIncidentId);
              next.set('review', '1');
              setSearchParams(next, { replace: true });
            }
          } catch {
            // Non-fatal: keep fast-path incident + fallback group context.
          }
        })();
      } catch {
        if (cancelled) return;
        setError(`Incident not found: ${incidentId}`);
      } finally {
        if (!cancelled) setDrawerIncidentLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setDrawerIncidentLoading(false);
    };
    // selectedIncident?.incident_id is intentionally omitted — URL drives hydration; re-running on
    // selection changes caused heatmap occurrence opens to snap back to the representative.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchParams,
    setError,
    setSearchParams,
    setSelectedGroupContext,
    setSelectedIncident,
    setViewDialogOpen,
    setViewDialogTab,
    timeRange.fromDate,
    timeRange.toDate,
  ]);

  const acknowledgeIncidentDeepLink = useCallback((loadKey: string) => {
    lastIncidentDeepLinkLoadRef.current = String(loadKey || '').trim();
  }, []);

  const openIncidentDrawer = useCallback(async (row: Incident, tab: number) => {
    const incidentId = String(row?.incident_id || '').trim();
    setSelectedGroupContext(null);
    setViewDialogTab(tab);
    setViewDialogOpen(true);
    if (!incidentId) {
      setSelectedIncident(row);
      return;
    }

    setDrawerIncidentLoading(true);
    try {
      const incident = await incidentsApi.getIncident(incidentId);
      setSelectedIncident(incident);
    } catch {
      // Fall back to the list row so the drawer still opens.
      setSelectedIncident(row);
    } finally {
      setDrawerIncidentLoading(false);
    }
  }, [
    setDrawerIncidentLoading,
    setSelectedGroupContext,
    setSelectedIncident,
    setViewDialogOpen,
    setViewDialogTab,
  ]);

  const openGroupedIncidentDrawer = useCallback(async (group: any) => {
    setSelectedGroupContext(group as any);
    setSelectedIncident(null);
    setViewDialogTab(0);
    setViewDialogOpen(true);

    setDrawerIncidentLoading(true);
    try {
      const linkedIncidentId = String(group?.linked_detailed_record_id || group?.incident_id || '').trim();
      const serverAnchorIncidentId = String(group?.review_anchor_incident_id || '').trim();
      const hasServerAnchorDecision = Boolean(
        String(group?.review_anchor_selection_reason || '').trim() && serverAnchorIncidentId,
      );
      if (hasServerAnchorDecision) {
        const incident = await incidentsApi.getIncident(serverAnchorIncidentId);
        setSelectedIncident(incident);
        return;
      }
      const statusFilter = tabValue === 1 ? 'open' : tabValue === 2 ? 'closed' : undefined;
      const result = await incidentsApi.listIncidents(
        statusFilter as any,
        (filterSeverity as any) || undefined,
        25,
        0,
        timeRange.fromDate,
        timeRange.toDate,
        group?.incident_type || undefined,
        group?.zone_id || undefined,
        group?.device_id || undefined,
        group?.camera_id || undefined,
        {
          summary: true,
          skip_total: true,
        },
      );
      const decision = pickGroupedEvidenceRepresentativeIncident(result.incidents || [], {
        fallbackId: linkedIncidentId,
        preferCloudProof: true,
        patternKey: String(group?.pattern_key || '').trim(),
      });
      setSelectedGroupContext({
        ...group,
        review_anchor_incident_id: decision.reviewAnchorIncidentId,
        review_anchor_selection_reason: decision.reviewAnchorSelectionReason,
        latest_incident_id: decision.latestIncidentId,
        latest_incident_evidence_state: decision.latestIncidentEvidenceState,
        anchor_reuse_allowed: decision.anchorReuseAllowed,
      } as any);
      const pickedIncidentId = decision.reviewAnchorIncidentId;
      if (pickedIncidentId) {
        const incident = await incidentsApi.getIncident(pickedIncidentId);
        setSelectedIncident(incident);
        return;
      }

      const latest = (result.incidents || [])[0];
      if (latest?.incident_id) {
        const incident = await incidentsApi.getIncident(latest.incident_id);
        setSelectedIncident(incident);
      }
    } finally {
      setDrawerIncidentLoading(false);
    }
  }, [
    filterSeverity,
    setDrawerIncidentLoading,
    setSelectedGroupContext,
    setSelectedIncident,
    setViewDialogOpen,
    setViewDialogTab,
    tabValue,
    timeRange.fromDate,
    timeRange.toDate,
  ]);

  const openIncidentDetails = useCallback((row: Incident | any) => {
    if (row && (String(row?.group_key || '').trim() || row?.is_grouped)) {
      void openGroupedIncidentDrawer(row);
      return;
    }
    void openIncidentDrawer(row, 0);
  }, [openGroupedIncidentDrawer, openIncidentDrawer]);

  const openIncidentJson = useCallback((row: Incident) => {
    void openIncidentDrawer(row, 1);
  }, [openIncidentDrawer]);

  const handleTableRowClick = useCallback(async (params: any) => {
    try {
      if (tableMode === 'grouped') {
        await openGroupedIncidentDrawer(params.row as any);
        return;
      }

      const row = params.row as any;
      const incidentId = String(row?.incident_id || '');
      if (!incidentId) return;
      const incident = await incidentsApi.getIncident(incidentId);
      setSelectedGroupContext(null);
      setSelectedIncident(incident);
      setViewDialogTab(0);
      setViewDialogOpen(true);
    } catch {
      // Non-fatal
    }
  }, [
    tableMode,
    setSelectedGroupContext,
    setSelectedIncident,
    setViewDialogTab,
    setViewDialogOpen,
    openGroupedIncidentDrawer,
  ]);

  useEffect(() => {
    const workflowId = String(searchParams.get('workflow_id') || '').trim();
    const incidentId = String(searchParams.get('incident_id') || '').trim();
    const caseId = String(searchParams.get('case_id') || searchParams.get('safety_case_id') || '').trim();
    const reviewMode = String(searchParams.get('review') || '').trim() === '1';
    if (!reviewMode || !scopedSiteId) return;

    if (workflowId && !incidentId) {
      let cancelled = false;
      void (async () => {
        try {
          const response = await reportsApi.getSafetyDashboard({
            org_id: scopedOrgId || undefined,
            site_id: scopedSiteId,
            limit: 100,
            report_from: searchParams.get('report_from') || undefined,
            report_to: searchParams.get('report_to') || undefined,
          });
          if (cancelled) return;
          const row = findSafetyDashboardWorkflowRowForReview(response.workflows, { workflowId, caseId });
          const representativeIncidentId = String(row?.representative_incident_id || '').trim();
          if (!representativeIncidentId) return;
          const next = new URLSearchParams(searchParams);
          next.set('incident_id', representativeIncidentId);
          if (row?.zone_id && !next.get('zone_id')) next.set('zone_id', String(row.zone_id));
          if (row?.camera_id && !next.get('camera_id')) next.set('camera_id', String(row.camera_id));
          if (row?.device_id && !next.get('device_id')) next.set('device_id', String(row.device_id));
          if (row?.risk_type && !next.get('policy_type')) next.set('policy_type', String(row.risk_type));
          setSearchParams(next, { replace: true });
        } catch {
          // Non-fatal: review can still open once incident context is chosen manually.
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    if (!workflowId && (incidentId || caseId)) {
      let cancelled = false;
      void (async () => {
        try {
          const response = await reportsApi.getSafetyDashboard({
            org_id: scopedOrgId || undefined,
            site_id: scopedSiteId,
            limit: 100,
            report_from: searchParams.get('report_from') || undefined,
            report_to: searchParams.get('report_to') || undefined,
          });
          if (cancelled) return;
          const row = findSafetyDashboardWorkflowRowForReview(response.workflows, { incidentId, caseId });
          const resolvedWorkflowId = String(row?.workflow_id || '').trim();
          if (!resolvedWorkflowId) return;
          const next = new URLSearchParams(searchParams);
          next.set('workflow_id', resolvedWorkflowId);
          const resolvedCaseId = String(row?.safety_case_id || row?.finding_id || '').trim();
          if (resolvedCaseId && !next.get('case_id')) next.set('case_id', resolvedCaseId);
          if (row?.representative_incident_id && !next.get('incident_id')) {
            next.set('incident_id', String(row.representative_incident_id));
          }
          setSearchParams(next, { replace: true });
        } catch {
          // Non-fatal: review can still open without workflow spine backfill.
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    return undefined;
  }, [scopedOrgId, scopedSiteId, searchParams, setSearchParams]);

  return {
    openIncidentDetails,
    openIncidentJson,
    handleTableRowClick,
    acknowledgeIncidentDeepLink,
  };
}
